import React from 'react';
import { ClassNameProps, ColumnInfo, ColumnType } from '@/Types';
import cn from 'classnames';
import styles from './Table.scss';
import { Column, DataGrid, IColumnProps } from 'devextreme-react/data-grid';
import { tableUtils } from '@/Components/Table/services/utils';

type Props<T> = {
  columns: Pick<ColumnInfo<T>, 'headerText' | 'columnType' | 'fieldName' | 'render' | 'width'>[];
  rows: T[];
  key: keyof T;
  id?: string;
} & ClassNameProps;

export const Table = <T extends object>({ columns, rows, className, key, id }: Props<T>): React.ReactElement => {
  const cellRender = (render: ((rowData: T, rowIndex: number) => React.ReactNode) | undefined) => {
    return render ? (params: { data: T; rowIndex: number }) => render(params.data, params.rowIndex) : undefined;
  };

  const customizeText = (columnType?: ColumnType) => (cellInfo: any) => {
    if (columnType !== undefined) return tableUtils.getColumnRender(columnType, cellInfo.value);
  };

  const generateColumns = (columnInfos?: ColumnInfo<T>[]) => {
    return columnInfos?.map(({ render, columnType, fieldName, headerText, width, children }) => {
      if (children?.length) {
        return (
          <Column key={`${fieldName as string}`} caption={headerText}>
            {generateColumns(children)}
          </Column>
        );
      } // if

      const props: IColumnProps & { key: string } = {
        key: `${fieldName as string}`,
        dataField: fieldName,
        caption: headerText,
        width: width,
        customizeText: customizeText(columnType),
        dataType: tableUtils.getTypeByColumnInfo(columnType),
        cellRender: cellRender(render),
        allowSorting: false,
      };

      return <Column {...props} key={`${fieldName as string}`}></Column>;
    });
  };

  return (
    <div className={cn(styles.tableWrapper, className)} id={id}>
      <DataGrid
        className={cn(className, styles.table)}
        width={'100%'}
        id="dataGrid"
        keyExpr={key as string}
        height={'100%'}
        dataSource={rows}
        showBorders
        showRowLines
        columnAutoWidth
        allowColumnResizing
        columnResizingMode={'widget'}
        scrolling={{
          useNative: true,
        }}
      >
        {generateColumns(columns)}
      </DataGrid>
    </div>
  );
};
